import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react";
import { Selector } from "../Select";
import emissionViewStore from "../../../Store/marco/emissionViewStore";
import { emissionActivityModalAPI } from "../../API/emissionActivityAPI";
import styles from "../../modals/styles/EmissionActivityModal.module.css";
import { SelectCom } from "./SelectCom";
import { buildParamValue } from "./utils/buildParamValue";
import { shouldShow } from "./utils/shouldShow";
import { useTranslation } from "react-i18next";
import {
  FormHelperText,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import commonStore from "../../../Store/marco/commonStore";
import { isPercentageInRange } from "../../utils/percentageValue";

const checkNum = function (num) {
  const n = Number(num); // 先轉成數字
  if (isNaN(n)) return ""; // 避免輸入非數字
  if (n < 0) return 0.001;
  if (n > 100) return 100;
  return n;
};

// 特殊處理百分比輸入
const normalizePercentageInput = (val) => {
  const raw = String(val).replace(/^0+(\d)/, "$1"); // 去掉前導零
  return checkNum(raw);
};

export const EAFormComponent = observer(({ EASchema, target }) => {
  const { t } = useTranslation("ActivityMain");

  const {
    getEAValue,
    _handleSetEAFormData,
    isEditEmissionActivity,
    _handleSetEmissionActivityModalLoading,
  } = emissionViewStore;

  const EAFormData = emissionViewStore[target];
  const isDirectApiEntry = getEAValue(
    "isDirectApiEntry",
    "isDirectApiEntry",
    target,
  );

  const isFieldDisabled = isDirectApiEntry;
  const isDisabledForGHG3_1 =
    isEditEmissionActivity &&
    EASchema.title === "3.1 Purchased Goods and Services";

  //////// GHG 3.6 calculationId 不能上鎖
  const unLockGHG3_6CalculationId = EASchema.title === "3.6 Business Travel";

  const [optionsMap, setOptionsMap] = useState({}); // key -> options

  const depsKey = useMemo(() => {
    return (EASchema?.fields || [])
      .filter((f) => f.type === "select") // ★ 只挑 select
      .map((f) => {
        const param = buildParamValue(EAFormData, f);
        const shown = shouldShow(f, EAFormData, null) ? 1 : 0;
        return `${f.key}:${String(param)}:${shown}`;
      })
      .join("|");
  }, [EASchema, EAFormData]);

  useEffect(() => {
    if (!EASchema) return;

    _handleSetEmissionActivityModalLoading(true);

    const selects = (EASchema.fields || []).filter(
      (f) => f.type === "select" && shouldShow(f, EAFormData, null),
    );

    Promise.all(
      selects.map(async (f) => {
        const api = emissionActivityModalAPI[f.optionsAPI];
        if (typeof api !== "function") return [f.key, []];

        const paramValue = buildParamValue(EAFormData, f);
        const list = await api(paramValue);
        return [f.key, list || []];
      }),
    ).then((pairs) => {
      setOptionsMap((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
      _handleSetEmissionActivityModalLoading(false);
    });
  }, [depsKey]);

  if (!EASchema) return null;

  useEffect(() => {
    if (!EASchema?.fields) return;
    EASchema.fields
      .filter((f) => shouldShow(f, EAFormData, null))
      .forEach((field) => {
        if (
          field.type === "input" &&
          field.placeholder !== undefined &&
          field.placeholder !== null
        ) {
          const currentVal = getEAValue(field.level, field.key, target);
          if (currentVal === undefined || currentVal === null) {
            _handleSetEAFormData(
              EASchema,
              field.level,
              field.key,
              field.placeholder,
              null,
              target,
            );
          }
        }
      });
  }, [EAFormData, EASchema]);

  const renderField = (field, value) => {
    if (field.type === "select") {
      return (
        <Selector
          field={field.key}
          value={value}
          options={optionsMap?.[field.key] || []}
          placeholder=""
          onChange={(_, v, opt) =>
            _handleSetEAFormData(
              EASchema,
              field.level,
              field.key,
              v,
              opt,
              target,
            )
          }
          //////// for YY
          disabled={
            isFieldDisabled &&
            !(unLockGHG3_6CalculationId && field.key === "calculationId")
          }
          autoFillNum={field?.autoFillNum}
          width={field?.width}
          translation="ActivityMain"
        />
      );
    }

    ////////input
    if (field.type === "input") {
      if (field.percentageRange) {
        const { min, max, helperText } = field.percentageRange;
        const hasError = !isPercentageInRange(value, min, max);

        return (
          <>
            <TextField
              className={styles.percentageTextField}
              type="text"
              value={value ?? ""}
              disabled={isFieldDisabled}
              error={hasError}
              size="small"
              fullWidth
              onChange={(e) =>
                _handleSetEAFormData(
                  EASchema,
                  field.level,
                  field.key,
                  e.target.value,
                  null,
                  target,
                )
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">%</InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
                "aria-label": t(field.label),
              }}
            />
            <FormHelperText
              className={styles.percentageHelperText}
              error={hasError}
            >
              {t(helperText)}
            </FormHelperText>
          </>
        );
      }

      return (
        <input
          type={field.percentage ? "number" : "text"}
          value={value ?? ""}
          placeholder={null}
          //////// for YY
          disabled={isFieldDisabled}
          onChange={(e) => {
            if (field.percentage) {
              const fixed = normalizePercentageInput(e.target.value);
              e.target.value = fixed;
              _handleSetEAFormData(
                EASchema,
                field.level,
                field.key,
                fixed,
                null,
                target,
              );
            } else {
              _handleSetEAFormData(
                EASchema,
                field.level,
                field.key,
                e.target.value,
                null,
                target,
              );
            }
          }}
          className={styles.input}
        />
      );
    }

    ////////trigger
    if (field.type === "trigger") {
      const isEnum = Array.isArray(field.options) && field.options.length > 0;

      return (
        <div className={styles.radioGroup} style={{ display: "flex" }}>
          {(isEnum && field.options)?.map((opt) => (
            <label key={opt.value} className={styles.radioGroupLabel}>
              <input
                type="radio"
                name={field.key}
                checked={
                  value ? value === opt.value : opt.value === field.defaultValue
                }
                //////// for YY
                disabled={isFieldDisabled || isDisabledForGHG3_1}
                onChange={() => {
                  _handleSetEAFormData(
                    EASchema,
                    field.level,
                    field.key,
                    opt.value, // ⭐ 不再假設 boolean
                    null,
                    target,
                  );
                }}
                className={styles.radioInput}
              />
              <span style={{ marginLeft: "5px" }}>{t(opt.label)}</span>
            </label>
          ))}
        </div>
      );
    }

    ////////空白佔位
    if (field.type === "block") {
      return <></>;
    }
    return null;
  };

  return (
    <>
      <div className={styles.specialTitle}>{t(EASchema.EATitle)}</div>

      {/** 公司下拉式選單 */}
      {EASchema.name !== "tranField" && (
        <SelectCom
          isEdit={isEditEmissionActivity || !!isDirectApiEntry}
          schema={EASchema}
          value={EAFormData?.selectedCom?.[0]?.companyId ?? null}
        />
      )}

      {/** 動態欄位 */}
      {EASchema?.fields
        .filter((f) => shouldShow(f, EAFormData, null))
        .map((field) => {
          const value = getEAValue(field.level, field.key, target) ?? "";

          if (field.type === "title") {
            return (
              <div key={field.key} className={styles.specialTitle}>
                {t(field?.title)}
              </div>
            );
          }

          if (
            field.type === "select" ||
            field.type === "input" ||
            field.type === "trigger" ||
            field.type === "block"
          ) {
            return (
              <div
                key={field.key}
                className={
                  field.longInputWrap
                    ? styles.longInputWrap
                    : `${styles.inputWrap} ${
                        field.percentageRange
                          ? styles.percentageInputWrap
                          : ""
                      }`
                }
              >
                <div className={styles.tooltipWrap}>
                  <label className={styles.inputWrapLabel}>
                    {field?.required && (
                      <span style={{ color: "red" }}>* </span>
                    )}
                    {t(field?.label)}
                    {field.percentageRange && " (%)"}
                  </label>
                  {field?.tooltip && (
                    <Tooltip
                      className={styles.tooltip}
                      title={t(field?.tooltip)}
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: "14px",
                          },
                        },
                      }}
                    >
                      <InfoIcon className={styles.infoIcon} />
                    </Tooltip>
                  )}
                </div>

                {renderField(field, value)}
              </div>
            );
          }

          // fugitiveEmissionRate 顯示用
          if (field.type === "fugitiveEmissionRate") {
            return (
              <div key={field.key} className={styles.fugitiveEmissionRate}>
                <p>
                  {t(field.label)} : {value} %
                </p>
              </div>
            );
          }
        })}
    </>
  );
});
